import { useMutation, useQuery } from '@tanstack/react-query';
import { announceSavedOffline } from '@/stores/savedOffline';
import { fetchOrCached, isOfflineGap, NOT_ON_THIS_PC } from '@/offline/availability';
import { LoadingNote, OfflineDetails } from '@/components/OfflineDetails';
import { DocumentFrame } from '@/components/DocumentFrame';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { piecesOf } from '@/offline/pieces';
import { useSavedWork } from '@/offline/useSavedWork';
import { useEffect, useState } from 'react';
import { api, UNREACHABLE_MESSAGE } from '@/api/client';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import { AppIcon } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { ComposePanel } from '@/components/ComposePanel';
import {
  DeleteButton,
  HistoryPanel,
  ReverseButton,
  ReviewSummaryDialog,
  printDocument,
} from '@/components/documents';
import { DraftNotice } from '@/components/DraftNotice';
import { DrawLines, emptyDrawLine, type DrawLine } from '@/components/DrawLines';
import { useDraft } from '@/stores/drafts';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  OkNote,
  Pager,
  Select,
  StatusPill,
  Table,
  Tile,
  errorMessage,
  lineQuantity,
  money,
  rate,
  quantity,
  shortDate,
  todayIso,
} from '@/components/ui';
import { useSkuCatalog, useStockInvalidation, useStores } from '@/hooks/inventory';
import { awaitWork, retryWork, saveWork, savedWork, takeBack } from '@/offline/engine';
import type { NewOperation, Operation } from '@/offline/outbox';
import { confirmAction } from '@/stores/confirm';
import { useFarmScopedStore } from '@/hooks/useFarmScopedStore';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import type { ScreenIntent } from '@/stores/shellNav';
import { useScreenIntent } from '@/stores/shellNav';
import { VehicleField } from '@/components/VehicleField';
import { packedSuffix } from '@/packing';

const TRANSFERS = '/api/v1/inventory/transfers';

const LATER = 'Saved on this PC. It will be done when the connection is back.';

/**
 * What a queued transfer carries besides its request: the form as it was
 * typed. It is what the list shows for work not yet sent, and what Fix it
 * loads back (.docs/023).
 */
export interface QueuedTransferForm {
  header: {
    fromStoreId: string;
    toStoreId: string;
    transferDate: string;
    kind: string;
    vehicleNo: string;
    remarks: string;
  };
  lines: DrawLine[];
}

/**
 * Saves one action on a document through the outbox (.docs/023): answered
 * now with a line, kept on this PC without one. A refusal while the screen is
 * open is taken back out and thrown, for the screen to show.
 */
async function saveOne(op: NewOperation): Promise<'sent' | 'waiting'> {
  const saved = await saveWork([op]);
  // Kept for later: said so where it cannot be missed.
  if (saved.outcome === 'waiting') void announceSavedOffline({ what: op.describes, closeLabel: 'OK' });
  if (saved.outcome === 'sent' || saved.outcome === 'waiting') return saved.outcome;
  for (const o of saved.ops) if (o.state !== 'sent') await takeBack(o.id).catch(() => undefined);
  throw new Error(saved.ops.find((o) => o.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
}

/** A transfer saved on this PC, shown in the list until the server has it. */
function queuedRow(op: Operation, stores: ReadonlyArray<{ id: string; code: string }>): TransferRow {
  const form = op.form as QueuedTransferForm;
  const code = (id: string) => stores.find((s) => s.id === id)?.code ?? '';
  const total = form.lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  return {
    id: `op:${op.id}`,
    documentNo: 'Not sent yet',
    status: op.state === 'refused' ? 'REFUSED' : 'NOT_SENT',
    transferDate: form.header.transferDate,
    isReturn: form.header.kind === 'return',
    fromStoreCode: code(form.header.fromStoreId),
    toStoreCode: code(form.header.toStoreId),
    totalQty: String(total),
    totalValue: null,
  } as unknown as TransferRow;
}

const PAGE_SIZE = 50;

function storeLabel(s: { code: string; nameEn: string }): string {
  return `${s.code} — ${s.nameEn}`;
}

interface TransferDetail {
  id: string;
  documentNo: string;
  status: string;
  isReturn: boolean;
  fromStoreId: string;
  fromStore: string;
  fromStoreCode: string;
  toStoreId: string;
  toStore: string;
  toStoreCode: string;
  transferDate: string;
  vehicleNo: string | null;
  remarks: string | null;
  totalQty: string;
  totalValue?: string;
  version: number;
  postedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  lines: Array<{
    id: string;
    skuId: string;
    skuCode: string;
    skuName: string;
    qty: string;
    unitCost?: string;
    value?: string;
    batchId: string | null;
    batchNo: string | null;
    expiryDate: string | null;
  }>;
}

interface TransferRow {
  id: string;
  documentNo: string;
  status: string;
  isReturn: boolean;
  transferDate: string;
  fromStoreCode: string;
  toStoreCode: string;
  totalQty: string;
  totalValue?: string;
}

type ComposeState =
  | { mode: 'create'; intent?: ScreenIntent | undefined; seed?: TransferDetail | undefined }
  | { mode: 'edit'; detail: TransferDetail }
  /** Work the server refused, loaded back to be put right (.docs/023). */
  | { mode: 'fix'; op: Operation };

/** A transfer's own screen: what moved from where to where, and what has happened to it since. */
function TransferDocument({ detail, showCost }: { detail: TransferDetail; showCost: boolean }) {
  return (
    <>
      {detail.reversedAt && (
        <p className="docpage__note docpage__note--alert" role="note">
          Reversed on {shortDate(detail.reversedAt)}. Reason: {detail.reversalReason ?? '—'}
        </p>
      )}
      {detail.status === 'DRAFT' && (
        <p className="docpage__note" role="note">
          This is a draft. Nothing has moved yet.
        </p>
      )}

      <div className="tiles">
        <Tile label="Kind" value={detail.isReturn ? 'Return' : 'Transfer'} />
        <Tile label="Total quantity" value={quantity(detail.totalQty, 2)} />
        {showCost && <Tile label="Value" value={money(detail.totalValue)} />}
        <Tile label="Items" value={String(detail.lines.length)} />
      </div>

      <section className="stack docpage__section">
        <h3 className="formsection">Movement</h3>
        <dl className="detaillist">
          <dt>From</dt>
          <dd>
            {detail.fromStoreCode} — {detail.fromStore}
          </dd>
          <dt>To</dt>
          <dd>
            {detail.toStoreCode} — {detail.toStore}
          </dd>
          <dt>Date</dt>
          <dd>{shortDate(detail.transferDate)}</dd>
          <dt>Vehicle</dt>
          <dd>{detail.vehicleNo ?? '—'}</dd>
          <dt>Remarks</dt>
          <dd>{detail.remarks ?? '—'}</dd>
          {detail.postedAt && (
            <>
              <dt>Posted on</dt>
              <dd>{shortDate(detail.postedAt)}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">Items</h3>
        <Table
          count={detail.lines.length}
          empty="No items."
          head={
            <tr>
              <th>Item</th>
              <th className="num">Quantity</th>
              {showCost && <th className="num">Unit cost</th>}
              {showCost && <th className="num">Value</th>}
              <th>Lot</th>
            </tr>
          }
        >
          {detail.lines.map((l) => (
            <tr key={l.id}>
              <td>
                <span className="mono">{l.skuCode}</span> {l.skuName}
              </td>
              <td className="num">{lineQuantity(l.qty)}</td>
              {showCost && <td className="num">{rate(l.unitCost)}</td>}
              {showCost && <td className="num">{money(l.value)}</td>}
              <td className="dim">
                {l.batchNo ? `${l.batchNo}${l.expiryDate ? ` · expires ${shortDate(l.expiryDate)}` : ''}` : '—'}
              </td>
            </tr>
          ))}
        </Table>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">History</h3>
        <HistoryPanel path={`/api/v1/inventory/transfers/${detail.id}/history`} />
      </section>
    </>
  );
}

export function Transfers() {
  const intent = useScreenIntent('inv-xfer');
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const showCost = can('inv.price.read');
  const [compose, setCompose] = useState<ComposeState | null>(() =>
    intent?.action === 'create' && can('inv.transfer.create') ? { mode: 'create', intent } : null,
  );
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(intent?.recordId ?? null);
  // Sent here to open one transfer: from a stock card's line, or from Offline work once it has gone.
  const [openKey, setOpenKey] = useState<string | null>(() => intent?.recordId ?? null);
  const [note, setNote] = useState<string | null>(null);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const { data: stores } = useStores();

  // Work saved on this PC and not yet with the server leads the list
  // (.docs/023): what somebody is still waiting on, with no number yet.
  const outbox = useSavedWork();
  const queued = (outbox.data ?? [])
    .filter((op) => op.kind === 'transfer.create' && op.state !== 'sent' && op.form !== undefined)
    .reverse();
  const opFor = (row: TransferRow) => queued.find((op) => `op:${op.id}` === row.id);

  // Sent here from Offline work to put refused work right.
  useEffect(() => {
    const opId = intent?.fixOperation;
    if (!opId) return;
    void savedWork().then((ops) => {
      const op = ops.find((o) => o.id === opId);
      if (op?.form) setCompose({ mode: 'fix', op });
    });
  }, [intent?.fixOperation]);

  async function throwAway(op: Operation) {
    const ok = await confirmAction({
      title: 'Throw this work away?',
      message: `"${op.describes}" will be removed from this PC. The server will never see it.`,
      confirmLabel: 'Throw it away',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    await takeBack(op.id);
    setSelectedKey(null);
    setNote('Thrown away.');
  }

  const list = useQuery({
    queryKey: ['inv', 'transfers', farmIds, skip],
    queryFn: () => api.get('/api/v1/inventory/transfers', { take: PAGE_SIZE, skip, ...farmScopeQuery(farmIds) }),
  });
  const { data, isPending, error } = list;

  const detailQuery = useQuery({
    queryKey: ['inv', 'transfers', 'detail', openKey],
    queryFn: async () =>
      (await api.get(`/api/v1/inventory/transfers/${openKey}` as '/api/v1/inventory/transfers/{id}')) as unknown as TransferDetail,
    // A row saved on this PC has no transfer on the server yet.
    enabled: openKey !== null && !openKey.startsWith('op:'),
  });
  const { data: detail, error: detailError, isFetching: detailFetching, refetch: refetchDetail } = detailQuery;

  // Each goes through the outbox, carrying the version that was seen where the
  // server takes one (.docs/023).
  const post = useMutation({
    mutationFn: (doc: { id: string; documentNo: string; version?: number | undefined }) =>
      saveOne({
        kind: 'transfer.post',
        method: 'POST',
        path: `${TRANSFERS}/${doc.id}/post`,
        ...(doc.version ? { body: { version: doc.version } } : {}),
        describes: `Posting ${doc.documentNo}`,
        screen: 'inv-xfer',
      }),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Posted to the ledger.' : LATER); },
  });
  const del = useMutation({
    mutationFn: ({ id, version, documentNo }: { id: string; version: number; documentNo: string }) =>
      saveOne({
        kind: 'transfer.cancel',
        method: 'DELETE',
        path: `${TRANSFERS}/${id}?version=${version}`,
        describes: `Deleting draft ${documentNo}`,
        screen: 'inv-xfer',
      }),
    // A deleted draft has no page left to stand on: back to the list, saying so.
    onSuccess: (outcome) => { invalidate(); setSelectedKey(null); setOpenKey(null); setNote(outcome === 'sent' ? 'Draft deleted.' : LATER); },
  });
  const reverse = useMutation({
    mutationFn: ({ id, reason, documentNo }: { id: string; reason: string; documentNo: string }) =>
      saveOne({
        kind: 'transfer.reverse',
        method: 'POST',
        path: `${TRANSFERS}/${id}/reverse`,
        body: { reason },
        describes: `Reversing ${documentNo}`,
        screen: 'inv-xfer',
      }),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Reversed.' : LATER); },
  });

  const rows = skip === 0 ? [...queued.map((op) => queuedRow(op, stores ?? [])), ...(data?.items ?? [])] : (data?.items ?? []);

  /** Moving between the list and a transfer: what was said on one is not news on the other. */
  function show(key: string | null) {
    setOpenKey(key);
    if (key) setSelectedKey(key);
    setNote(null);
    post.reset();
    del.reset();
    reverse.reset();
  }

  /** The whole document: from the server, or as this PC last loaded it. */
  async function fetchFull(id: string): Promise<TransferDetail> {
    return fetchOrCached(
      ['inv', 'transfers', 'detail', id],
      async () => (await api.get(`/api/v1/inventory/transfers/${id}` as '/api/v1/inventory/transfers/{id}')) as unknown as TransferDetail,
      'This document',
    );
  }

  async function edit(row: TransferRow) {
    try {
      setCompose({ mode: 'edit', detail: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that transfer to edit it'));
    }
  }

  async function repeat(row: TransferRow) {
    setRepeatBusy(true);
    try {
      setCompose({ mode: 'create', seed: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that transfer to repeat it'));
    } finally {
      setRepeatBusy(false);
    }
  }

  async function print(row: TransferRow) {
    try {
      printTransfer(await fetchFull(row.id));
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that transfer to print it'));
    }
  }

  function printTransfer(full: TransferDetail) {
    printDocument({
      title: `${full.isReturn ? 'Return' : 'Transfer'} ${full.documentNo}`,
      facts: [
        ['Status', full.status],
        ['Kind', full.isReturn ? 'Return' : 'Transfer'],
        ['From', `${full.fromStoreCode} — ${full.fromStore}`],
        ['To', `${full.toStoreCode} — ${full.toStore}`],
        ['Date', shortDate(full.transferDate)],
        ['Vehicle', full.vehicleNo ?? '—'],
        ['Total quantity', quantity(full.totalQty, 2)],
        ...(showCost ? ([['Total value', money(full.totalValue)]] as Array<[string, string]>) : []),
      ],
      lines: full.lines.map((l) => ({
        item: `${l.skuCode} — ${l.skuName}`,
        qty: quantity(l.qty, 4),
        ...(l.unitCost ? { unitCost: rate(l.unitCost) } : {}),
        ...(l.value ? { value: money(l.value) } : {}),
        ...(l.batchNo ? { lot: l.batchNo } : {}),
      })),
      showCost,
    });
  }

  function rowActions(row: TransferRow): DesktopAction[] {
    const op = opFor(row);
    if (op) {
      return [
        { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
        {
          label: 'Fix it', icon: 'edit' as const,
          onSelect: () => setCompose({ mode: 'fix', op }),
          allowed: can('inv.transfer.create'),
          enabled: op.state === 'refused',
          reason: 'Only work the server refused needs fixing',
        },
        {
          label: 'Throw it away', icon: 'remove' as const,
          onSelect: () => void throwAway(op),
          allowed: true,
          enabled: op.state === 'refused',
          reason: 'Work still waiting to be sent cannot be thrown away',
        },
      ];
    }
    return [
      { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
      {
        label: 'Edit', icon: 'edit' as const,
        onSelect: () => void edit(row),
        allowed: can('inv.transfer.update'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be edited',
      },
      {
        label: 'Post', icon: 'post' as const,
        onSelect: () => post.mutate({ id: row.id, documentNo: row.documentNo, version: detail?.id === row.id ? detail.version : undefined }),
        allowed: can('inv.transfer.post'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be posted',
      },
      { label: 'Repeat', icon: 'repeat' as const, onSelect: () => void repeat(row), allowed: can('inv.transfer.create'), enabled: !repeatBusy, separatorBefore: true },
      { label: 'Print', icon: 'print' as const, onSelect: () => void print(row), allowed: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Move stock', icon: 'transfer', primary: true, onSelect: () => setCompose({ mode: 'create' }), allowed: can('inv.transfer.create') },
  ];

  const columns: Array<GridColumn<TransferRow>> = [
    { key: 'doc', header: 'Document', width: 150, render: (r) => <span className="withicon"><AppIcon kind="transfer" size={14} /><span className="mono">{r.documentNo}</span></span> },
    { key: 'date', header: 'Date', width: 100, render: (r) => shortDate(r.transferDate) },
    { key: 'from', header: 'From', width: 100, render: (r) => <span className="dim">{r.fromStoreCode}</span> },
    { key: 'to', header: 'To', width: 100, render: (r) => <span className="dim">{r.toStoreCode}</span> },
    { key: 'kind', header: 'Kind', width: 100, render: (r) => (r.isReturn ? <span className="pill"><AppIcon kind="returnGoods" size={12} />Return</span> : <span className="dim withicon"><AppIcon kind="transfer" size={14} />Transfer</span>) },
    { key: 'qty', header: 'Quantity', align: 'end', width: 100, render: (r) => quantity(r.totalQty, 2) },
    ...(showCost ? [{ key: 'value', header: 'Value', align: 'end' as const, width: 120, render: (r: TransferRow) => money(r.totalValue) }] : []),
    { key: 'status', header: 'Status', width: 110, render: (r) => <StatusPill status={r.status} /> },
  ];

  if (compose) {
    return (
      <TransferCompose
        state={compose}
        onCancel={() => setCompose(null)}
        onSaved={(message) => { setCompose(null); setNote(message); }}
      />
    );
  }

  // Work saved on this PC: shown as it was typed until the server has it.
  const savedPiece = openKey?.startsWith('op:')
    ? piecesOf(outbox.data ?? []).find((p) => p.ops.some((op) => `op:${op.id}` === openKey))
    : undefined;
  if (savedPiece) {
    return (
      <SavedWorkDetail
        piece={savedPiece}
        all={outbox.data ?? []}
        backLabel="Back to Transfers"
        onBack={() => show(null)}
        onFix={(op) => { show(null); setCompose({ mode: 'fix', op }); }}
        onOpenDocument={(id) => show(id)}
        onDone={(said) => { show(null); setNote(said); }}
      />
    );
  }

  if (openKey && !openKey.startsWith('op:')) {
    const openRow = rows.find((r) => r.id === openKey);
    const canEdit = detail?.status === 'DRAFT' && can('inv.transfer.update');
    const canDelete = detail?.status === 'DRAFT' && can('inv.transfer.delete');
    const canPost = detail?.status === 'DRAFT' && can('inv.transfer.post');
    const canReverse = detail?.status === 'POSTED' && can('inv.transfer.reverse');
    return (
      <DocumentFrame
        onBack={() => show(null)}
        backLabel="Back to Transfers"
        title={
          detail && (
            <>
              <h2 className="withicon">
                <AppIcon kind={detail.isReturn ? 'returnGoods' : 'transfer'} size={18} />
                <span className="mono">{detail.documentNo}</span>
              </h2>
              <StatusPill status={detail.status} />
              <span className="muted">
                {detail.fromStoreCode} → {detail.toStoreCode} · {shortDate(detail.transferDate)}
              </span>
            </>
          )
        }
        actions={
          detail && (
            <>
              <button type="button" className="btn btn--withicon" onClick={() => printTransfer(detail)}>
                <AppIcon kind="print" size={15} />
                Print
              </button>
              {can('inv.transfer.create') && (
                <button
                  type="button"
                  className="btn btn--withicon"
                  onClick={() => {
                    // Leaves this transfer behind: "Back to list" means the list.
                    show(null);
                    setCompose({ mode: 'create', seed: detail });
                  }}
                >
                  <AppIcon kind="repeat" size={15} />
                  Repeat
                </button>
              )}
              {canDelete && (
                <DeleteButton pending={del.isPending} onDelete={() => del.mutate({ id: detail.id, version: detail.version, documentNo: detail.documentNo })} />
              )}
              {canReverse && (
                <ReverseButton pending={reverse.isPending} onReverse={(reason) => reverse.mutate({ id: detail.id, reason, documentNo: detail.documentNo })} />
              )}
              {canEdit && (
                <button type="button" className="btn btn--withicon" onClick={() => setCompose({ mode: 'edit', detail })}>
                  <AppIcon kind="edit" size={15} />
                  Edit
                </button>
              )}
              {canPost && (
                <button
                  type="button"
                  className="btn btn--primary btn--withicon"
                  disabled={post.isPending || detailFetching}
                  onClick={() => post.mutate({ id: detail.id, documentNo: detail.documentNo, version: detail.version })}
                >
                  <AppIcon kind="post" size={15} />
                  {post.isPending ? 'Posting…' : 'Post'}
                </button>
              )}
            </>
          )
        }
      >
        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {detailError && !detail && <ErrorNote message={detailError.message} />}
        {!detail && isOfflineGap(detailQuery) && openRow && <OfflineDetails row={openRow} columns={columns} />}
        {!detail && !detailError && !(isOfflineGap(detailQuery) && openRow) && <LoadingNote what="Loading the transfer…" />}
        {detail && <TransferDocument detail={detail} showCost={showCost} />}
      </DocumentFrame>
    );
  }

  return (
    <div className="workbench">
      <div className="workbench__main">
        <ActionBar actions={toolbarActions} />

        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {error && <ErrorNote message={error.message} />}

        <DataGrid
          id="documents-transfers"
          label="Transfers"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          // A row saved on this PC opens as what was saved, and where it has got to.
          onOpen={(row) => show(row.id)}
          openOnClick
          actions={rowActions}
          loading={isPending && !isOfflineGap(list)}
          empty={isOfflineGap(list) ? NOT_ON_THIS_PC : 'No transfers yet.'}
        />
        <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={setSkip} />
      </div>
    </div>
  );
}

function TransferCompose({
  state,
  onCancel,
  onSaved,
}: {
  state: ComposeState;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const catalog = useSkuCatalog();

  const initial = state.mode === 'edit' ? state.detail : undefined;
  const seed = initial ?? (state.mode === 'create' ? state.seed : undefined);
  const intent = state.mode === 'create' ? state.intent : undefined;
  const editing = initial !== undefined;
  // Refused work being put right: its form comes back as it was typed.
  const fixing = state.mode === 'fix' ? state.op : undefined;
  const fixForm = fixing?.form as QueuedTransferForm | undefined;
  const canPost = can('inv.transfer.post');

  const [header, setHeader] = useState(() => fixForm ? { ...fixForm.header } : ({
    fromStoreId: seed?.fromStoreId ?? intent?.storeId ?? '',
    toStoreId: seed?.toStoreId ?? '',
    transferDate: initial ? initial.transferDate.slice(0, 10) : todayIso(),
    kind: seed?.isReturn ? 'return' : 'transfer',
    vehicleNo: initial?.vehicleNo ?? '',
    remarks: initial?.remarks ?? '',
  }));
  const [lines, setLines] = useState<DrawLine[]>(() =>
    fixForm
      ? fixForm.lines.map(({ key: _old, ...line }) => emptyDrawLine(line))
      : seed && seed.lines.length > 0
      ? seed.lines.map((l) =>
          emptyDrawLine({
            skuId: l.skuId,
            skuLabel: `${l.skuCode} — ${l.skuName}`,
            qty: initial ? l.qty : '',
            batchId: l.batchId ?? '',
          }),
        )
      : [emptyDrawLine({ skuId: intent?.skuId ?? '', batchId: intent?.batchId ?? '' })],
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  // Typing survives a reload, a crash or a closed window. Editing one document
  // must not offer another one's typing, so the draft is named after it.
  const draft = useDraft(
    editing ? `transfer.edit.${initial.id}` : fixing ? `transfer.fix.${fixing.id}` : 'transfer.new',
    { header, lines },
    (saved) => {
      setHeader(saved.header);
      // Row keys come from a counter that restarts with the page, so restored
      // rows take fresh ones rather than colliding with a later form's.
      setLines(saved.lines.map(({ key: _old, ...rest }) => emptyDrawLine(rest)));
    },
  );

  function startAgain() {
    setHeader((h) => ({ ...h, remarks: '' }));
    setLines([emptyDrawLine()]);
    setProblem(null);
  }

  // A new source that is the current destination clears the destination,
  // rather than leaving a store sending stock to itself.
  function changeFromStore(fromStoreId: string) {
    setHeader((h) =>
      h.fromStoreId === fromStoreId
        ? h
        : { ...h, fromStoreId, toStoreId: h.toStoreId === fromStoreId ? '' : h.toStoreId },
    );
  }

  const { options: fromStoreOptions } = useFarmScopedStore(stores, header.fromStoreId, changeFromStore);

  const startedLines = lines.filter((l) => l.skuId || l.qty);

  function validate(): string | null {
    if (!header.fromStoreId) return 'Choose the source store.';
    if (!header.toStoreId) return 'Choose the destination store.';
    if (header.fromStoreId === header.toStoreId) return 'A store cannot send stock to itself.';
    if (startedLines.length === 0) return 'Add at least one item.';
    if (startedLines.some((l) => !l.skuId)) return 'Every line with a quantity needs an item.';
    if (startedLines.some((l) => !(Number(l.qty) > 0))) return 'Every item needs a quantity above zero.';
    if (startedLines.some((l) => catalog.known.get(l.skuId)?.isBatchTracked && !l.batchId)) {
      return 'Choose which lot each lot-tracked item is moved from.';
    }
    return null;
  }

  const save = useMutation({
    mutationFn: async (andPost: boolean): Promise<{ documentNo: string; posted: boolean; waiting: boolean; warning?: string }> => {
      const body = {
        fromStoreId: header.fromStoreId,
        toStoreId: header.toStoreId,
        transferDate: header.transferDate,
        isReturn: header.kind === 'return',
        vehicleNo: header.vehicleNo.trim() || undefined,
        remarks: header.remarks.trim() || undefined,
        lines: startedLines.map((l) => ({ skuId: l.skuId, qty: l.qty, ...(l.batchId ? { batchId: l.batchId } : {}) })),
      };
      const form = { header, lines: startedLines } satisfies QueuedTransferForm;
      const describes = `${header.kind === 'return' ? 'Return' : 'Transfer'} ${shortDate(header.transferDate)}: ${startedLines.length} item(s)`;

      if (fixing) {
        // Sent again under a new key, with its post following it.
        const newId = await retryWork(fixing.id, { body, form, describes });
        const saved = newId ? await awaitWork([newId]) : null;
        if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
          throw new Error(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
        }
        const made = saved.ops[0]?.result as { documentNo?: string } | undefined;
        return { documentNo: made?.documentNo ?? '', posted: false, waiting: saved.outcome === 'waiting' };
      }

      // Through the outbox (.docs/023): answered now with a line, kept on this
      // PC without one. A change carries the version that was seen; the post
      // after it carries the version the change makes.
      const batch: NewOperation[] = initial
        ? [{ kind: 'transfer.edit', method: 'PATCH', path: `${TRANSFERS}/${initial.id}`, body: { version: initial.version, ...body }, describes: `Changing ${initial.documentNo}`, screen: 'inv-xfer' }]
        : [{ kind: 'transfer.create', method: 'POST', path: TRANSFERS, body, describes, screen: 'inv-xfer', form }];
      if (andPost) {
        batch.push(
          initial
            ? { kind: 'transfer.post', method: 'POST', path: `${TRANSFERS}/${initial.id}/post`, body: { version: initial.version + 1 }, describes: `Posting ${initial.documentNo}`, screen: 'inv-xfer' }
            : { kind: 'transfer.post', method: 'POST', path: `${TRANSFERS}/{dep}/post`, after: 0, describes: `Posting the ${describes}`, screen: 'inv-xfer' },
        );
      }

      const saved = await saveWork(batch);
      const made = (initial ? { documentNo: initial.documentNo } : saved.ops[0]?.result) as { documentNo?: string } | undefined;
      const documentNo = made?.documentNo ?? '';
      if (saved.outcome === 'sent') return { documentNo, posted: andPost, waiting: false };
      if (saved.outcome === 'waiting') return { documentNo, posted: andPost, waiting: true };

      // Refused while this form is open: said here rather than queued up for
      // Needs attention too. What the server took stays.
      for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
      const reason = saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE;
      if (!initial && saved.ops[0]?.state === 'sent') {
        // The transfer was made and only its post was refused: the form must
        // not make another. Post the draft from the list.
        return { documentNo, posted: false, waiting: false, warning: `${documentNo} was saved as a draft but not posted: ${reason}. Post it from the list.` };
      }
      throw new Error(reason);
    },
    onSuccess: (result) => {
      invalidate();
      const text = result.warning
        ?? (result.waiting
          ? LATER
          : result.posted
            ? `${result.documentNo} moved. Stock has left the source and arrived at the destination.`
            : `${result.documentNo} saved as a draft.`);
      draft.forget();
      const what = editing ? `The change to ${initial.documentNo}` : fixing ? fixing.describes : `The transfer of ${lines.filter((l) => l.skuId).length} item(s)`;
      if (editing || fixing) {
        onSaved(text);
        if (result.waiting) void announceSavedOffline({ what, closeLabel: 'OK' });
        return;
      }
      setMessage(text);
      setLines([emptyDrawLine()]);
      setHeader((h) => ({ ...h, remarks: '' }));
      if (result.waiting) {
        void announceSavedOffline({ what, canEnterAnother: true, note: 'It gets its document number when it is sent.' }).then((choice) => {
          if (choice !== 'another') onSaved(text);
        });
      }
    },
  });

  function submit(andPost: boolean) {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) save.mutate(andPost);
  }

  function openReview() {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) setReviewing(true);
  }

  const fromStoreName = fromStoreOptions.find((s) => s.id === header.fromStoreId);
  const toStoreName = (stores ?? []).find((s) => s.id === header.toStoreId);

  const panel = (
    <ComposePanel
      icon="transfer"
      splitId="compose-transfer"
      title={editing ? `Edit ${initial.documentNo}` : fixing ? 'Put right a refused transfer' : 'Move stock between stores'}
      intro="Posting takes the stock out of the source and puts it into the destination in one step, at the value it left with."
      onCancel={() => {
        draft.forget();
        onCancel();
      }}
      cancelLabel={editing ? 'Cancel' : 'Back to list'}
      top={
        <div className="stack">
          <DraftNotice draft={draft} onStartAgain={startAgain} />
          <FormGroup title="Route" template="1.5fr 1.5fr 1fr 1.3fr 1fr">
            <StorePicker
              label="From store"
              required
              value={header.fromStoreId}
              onChange={changeFromStore}
              stores={stores && fromStoreOptions}
            />
            <StorePicker
              label="To store"
              required
              value={header.toStoreId}
              onChange={(toStoreId) => setHeader((h) => ({ ...h, toStoreId }))}
              stores={stores?.filter((s) => s.id !== header.fromStoreId)}
            />
            <Field label="Date" type="date" required value={header.transferDate} onChange={(transferDate) => setHeader((h) => ({ ...h, transferDate }))} />
            <Select
              label="Kind"
              required
              value={header.kind}
              onChange={(kind) => setHeader((h) => ({ ...h, kind }))}
              options={[
                { value: 'transfer', label: 'Transfer' },
                { value: 'return', label: 'Return' },
              ]}
              placeholder=""
              hint="A return sends unused stock back, e.g. from a farm to the main store"
            />
            <VehicleField label="Vehicle" value={header.vehicleNo} onChange={(vehicleNo) => setHeader((h) => ({ ...h, vehicleNo }))} hint="Registration number, if it went by road" />
            <Field label="Remarks" value={header.remarks} onChange={(remarks) => setHeader((h) => ({ ...h, remarks }))} span="full" />
          </FormGroup>
        </div>
      }
      bottom={
        <>
          <FormGroup title="What is moving" hint={header.fromStoreId ? 'Search by item code or name.' : 'Choose the source store first.'}>
            <DrawLines
              lines={lines}
              onChange={setLines}
              skus={catalog.known}
              search={catalog.search}
              storeId={header.fromStoreId}
            />
          </FormGroup>

        </>
      }
      footer={
        <>
          {problem && <ErrorNote message={problem} />}
          {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}
          {message && <OkNote message={message} />}

          <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
            <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={openReview}>
              Review & Save
            </button>
          </FormActions>
        </>
      }
    />
  );

  return (
    <>
      {panel}
      {reviewing && (
        <ReviewSummaryDialog
          icon="transfer"
          title={editing ? `Review ${initial.documentNo}` : 'Review before moving'}
          facts={[
            ['From store', fromStoreName ? storeLabel(fromStoreName) : '—'],
            ['To store', toStoreName ? storeLabel(toStoreName) : '—'],
            ['Date', header.transferDate],
            ['Kind', header.kind === 'return' ? 'Return' : 'Transfer'],
            ['Vehicle', header.vehicleNo.trim() || '—'],
            ['Remarks', header.remarks.trim() || '—'],
          ]}
          lines={startedLines.map((l) => {
            const sku = catalog.known.get(l.skuId);
            return {
              item: l.skuLabel || sku?.code || l.skuId,
              qty: sku ? `${quantity(l.qty, sku.uomPrecision)}${packedSuffix(l.qty, sku)}` : l.qty,
            };
          })}
          onClose={() => setReviewing(false)}
          primary={{
            label: canPost ? (editing ? 'Save and post' : 'Move and post') : editing ? 'Save changes' : 'Save draft',
            pending: save.isPending,
            onClick: () => {
              setReviewing(false);
              submit(canPost);
            },
          }}
          secondary={
            canPost
              ? {
                  label: editing ? 'Save changes' : 'Save draft',
                  pending: save.isPending,
                  onClick: () => {
                    setReviewing(false);
                    submit(false);
                  },
                }
              : undefined
          }
        />
      )}
    </>
  );
}
